import React from 'react';
import { ArrowLeft, Loader, CheckCircle, Link2 } from 'lucide-react';

const WikimediaImagePreview = ({ image, onUseImage, onBack, isProcessing }) => {
  // Strip HTML tags from attribution for plain text display
  const stripHtml = (html) => {
    if (!html) return '';
    if (typeof DOMParser === 'undefined') {
      return String(html).replace(/<[^>]*>/g, '').trim();
    }
    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    return parsed.body?.textContent?.trim() || '';
  };

  const cc = image.ccData || {};
  const hasCcData = Boolean(
    cc.artist ||
    cc.credit ||
    cc.attribution ||
    cc.licenseShortName ||
    cc.licenseUrl ||
    cc.usageTerms ||
    cc.source ||
    cc.pageUrl
  );

  return (
    <div className="space-y-4">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-blue-500 hover:text-blue-600 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to results
        </button>
      )}

      <div className="relative rounded-lg overflow-hidden border">
        <img
          src={image.thumbUrl}
          alt={image.filename}
          className="w-full h-64 object-contain bg-gray-100"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999"%3EImage Preview Unavailable%3C/text%3E%3C/svg%3E';
          }}
        />
      </div>

      <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-base mb-2">Image Details</h3>

        <div className="grid grid-cols-[100px_1fr] gap-2">
          <span className="font-medium text-gray-700">File:</span>
          <span className="break-all">{image.filename}</span>

          <span className="font-medium text-gray-700">License:</span>
          <span className="flex items-center gap-1">
            {image.license}
            {image.license !== 'Unknown' && (
              <CheckCircle className="w-3 h-3 text-green-500" />
            )}
          </span>

          {(cc.attribution || image.attribution) && (
            <>
              <span className="font-medium text-gray-700">Credit:</span>
              <span className="text-xs">{stripHtml(cc.attribution || image.attribution)}</span>
            </>
          )}

          {cc.artist && (
            <>
              <span className="font-medium text-gray-700">Author:</span>
              <span className="text-xs">{stripHtml(cc.artist)}</span>
            </>
          )}

          {cc.licenseUrl && (
            <>
              <span className="font-medium text-gray-700">License URL:</span>
              <a
                href={cc.licenseUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline break-all"
              >
                {cc.licenseUrl}
              </a>
            </>
          )}

          {cc.pageUrl && (
            <>
              <span className="font-medium text-gray-700">Source:</span>
              <a
                href={cc.pageUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline break-all flex items-center gap-1"
              >
                <Link2 className="w-3 h-3" />
                Wikimedia file page
              </a>
            </>
          )}

          {cc.usageTerms && (
            <>
              <span className="font-medium text-gray-700">Usage Terms:</span>
              <span className="text-xs">{stripHtml(cc.usageTerms)}</span>
            </>
          )}

          <span className="font-medium text-gray-700">Dimensions:</span>
          <span>{image.width} × {image.height} px</span>

          <span className="font-medium text-gray-700">File Size:</span>
          <span>
            {(image.size / 1024).toFixed(1)} KB
            {image.size > 500 * 1024 && (
              <span className="text-orange-600 ml-2 text-xs">
                (May be reduced during download)
              </span>
            )}
          </span>
        </div>

        {image.attributionRequired && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <strong>Note:</strong> This image requires attribution under {image.license} license.
          </div>
        )}

        {hasCcData && (
          <div className="mt-3 p-3 bg-slate-100 border border-slate-200 rounded text-xs text-slate-700 space-y-1">
            <div className="font-medium text-slate-800">CC data collected</div>
            <div>License: {cc.licenseShortName || image.license || 'Unknown'}</div>
            <div>Attribution: {stripHtml(cc.attribution || image.attribution || 'Not specified')}</div>
            {cc.source && <div>Source: {stripHtml(cc.source)}</div>}
            {cc.pageUrl && <div>Page: {cc.pageUrl}</div>}
          </div>
        )}
      </div>

      <button
        onClick={onUseImage}
        disabled={isProcessing}
        className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
      >
        {isProcessing ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Downloading and uploading...
          </>
        ) : (
          <>
            <CheckCircle className="w-5 h-5" />
            Use This Image
          </>
        )}
      </button>
    </div>
  );
};

export default WikimediaImagePreview;
